import "./ResourceOptions.css";

import { DateTime } from "luxon";
import { ReactNode } from "react";

import {
  CaseAdmin,
  CaseReportSignature,
  CliaCertificate,
  Clinic,
  Icd10Code,
  InsurancePlan,
  Lab,
  Patient,
  PatientFullInsurance,
  UserList,
  UserName,
} from "pwapi";

import { StainList } from "custom-models/frontLoader";

type OptionProps = {
  index?: string | number;
  info: {
    main: string;
    mainSuffix?: string | ReactNode;
    description?: string | ReactNode;
  };
};

export const Option = (props: OptionProps) => {
  const { index, info } = props;

  return (
    <div className="SearchSelectOption option__layout--vertical">
      <div className="option__layout--horizontal">
        {index ? <code className="option__index">{index}</code> : <></>}
        <p className="option__text option__text--ellipsis">
          <strong>{info.main}</strong>
          <span className="option__main--suffix">{info.mainSuffix}</span>
        </p>
      </div>
      <p className="option__description option__text--ellipsis">
        {info.description}
      </p>
    </div>
  );
};

export const UserOption = ({ object }: { object: UserList | UserName }) => {
  const { first_name, last_name } = object;
  const user_type = (object as UserList).userprofile?.user_type;
  return (
    <Option
      info={{
        main: `${first_name} ${last_name}`,
        ...(user_type && { mainSuffix: <code>{` ${user_type || ""}`}</code> }),
        description: (object as UserList).email || "",
      }}
    />
  );
};

export const CaseReportSignatureOption = ({
  object,
}: {
  object: CaseReportSignature;
}) => {
  const { user, clia_certificate } = object;
  return (
    <Option
      index={clia_certificate.number}
      info={{
        main: `${user.first_name} ${user.last_name}${
          user.credentials ? `, ${user.credentials}` : ""
        }`,

        description: (
          <>
            {clia_certificate?.name} - {clia_certificate?.number}
            <br />
            {clia_certificate?.description}
            {clia_certificate?.location &&
              (clia_certificate.location.street ||
                clia_certificate.location.city ||
                clia_certificate.location.state) && (
                <>
                  <br />
                  {clia_certificate.location.street},{" "}
                  {clia_certificate.location.city},{" "}
                  {clia_certificate.location.state}
                </>
              )}
          </>
        ),
      }}
    />
  );
};

export const PatientOption = ({
  object,
}: {
  object: Patient | PatientFullInsurance;
}) => {
  const { id, first_name, last_name, clinic, date_of_birth, sex } = object;

  return (
    <Option
      index={id || ""}
      info={{
        main: `${first_name} ${last_name}`,
        mainSuffix: `, ${sex || ""} | ${date_of_birth || ""}`,
        description: clinic?.name || "",
      }}
    />
  );
};

export const CaseOption = ({ object }: { object: CaseAdmin }) => {
  const { id, accession, patient, created_at, status, slides } = object;
  return (
    <div className="SearchSelectOption option__layout--vertical">
      <div className="option__layout--horizontal">
        <code className="option__index">{id}</code>
        <p className="option__text option__text--ellipsis">
          <strong>{accession}</strong>
          <span> - {status}</span>
        </p>
      </div>
      <p className="option__description option__text--ellipsis">
        ({slides?.length} slide{slides && slides?.length > 1 && "s"}
        )&nbsp;-&nbsp;
        {created_at && <time>{new Date(created_at).toLocaleDateString()}</time>}
      </p>
      <p className="option__text option__text--ellipsis">
        {patient?.last_name},&nbsp;{patient?.first_name}&nbsp;(
        {patient?.date_of_birth && (
          <time className="option__description">
            {DateTime.fromFormat(patient.date_of_birth, "yyyy-MM-dd").toFormat(
              "MM/dd/yyyy"
            )}
          </time>
        )}
        )
      </p>
    </div>
  );
};

export const LabOption = ({ object: lab }: { object?: Lab }) => {
  return (
    <Option index={lab?.id || ""} info={{ main: lab?.name || "Missing Lab" }} />
  );
};

export const ClinicOption = ({ object: clinic }: { object: Clinic }) => {
  return (
    <Option
      index={clinic?.id || ""}
      info={{
        main: clinic?.name || "",
        mainSuffix: clinic?.is_archived ? (
          <code>
            <small> (ARCHIVED)</small>
          </code>
        ) : undefined,
        description: (
          <>
            {clinic.location && (
              <>
                {clinic.location.street}, {clinic.location.city},{" "}
                {clinic.location.state} {clinic.location.zipcode}
              </>
            )}
            {clinic.location?.street2 && (
              <>
                <br />
                {clinic.location.street2}
              </>
            )}
            {clinic.external_clinic_id && (
              <>
                <br />
                {clinic.external_clinic_id}
              </>
            )}
          </>
        ),
      }}
    />
  );
};

export const StainOption = ({ object: stain }: { object: StainList }) => {
  const { id, category, abbreviation, name } = stain;
  return (
    <Option
      index={id || ""}
      info={{
        main: `${abbreviation}`,
        mainSuffix: `- ${name}`,
        description: category,
      }}
    />
  );
};

export const CliaCertificateOption = ({
  object: certificate,
}: {
  object: CliaCertificate;
}) => {
  const { id, name, number, description } = certificate;
  return (
    <Option
      index={id || 0}
      info={{
        main: name,
        mainSuffix: number ? ` - ${number}` : "",
        description: description || "",
      }}
    />
  );
};

export const InsurancePlanOption = ({
  object: insurance,
}: {
  object: InsurancePlan;
}) => {
  const { id, payer_location, payer_name } = insurance;
  return (
    <Option
      index={id || 0}
      info={{
        main: payer_name || "",
        description: (
          <>
            {payer_location && (
              <>
                {payer_location.street}, {payer_location.city},{" "}
                {payer_location.state} {payer_location.zipcode}
              </>
            )}
          </>
        ),
      }}
    />
  );
};

export const Icd10Option = ({ object: icd10code }: { object: Icd10Code }) => {
  const { id, code, description } = icd10code;

  return (
    <Option
      index={id || 0}
      info={{
        main: code,
        description,
      }}
    />
  );
};
